import { ChakraProvider } from "@chakra-ui/react";
import reduxWebsocket from "@giantmachines/redux-websocket";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React from "react";
import { Provider, useSelector } from "react-redux";
import { Route, Routes } from "react-router";
import { AnyAction, applyMiddleware, combineReducers, compose, createStore } from "redux";
import thunk, { ThunkAction, ThunkDispatch } from "redux-thunk";

import {
  CognitoCallbackPage,
  Fonts,
  IdentityProviderChooser,
  SystemStatusPage,
  TenantProvider,
  getRanaTheme,
  retrieveTenantAndOrganisation,
  storeTenantAndOrganisation,
} from "@nens/ranalib";
import { SentryErrorBoundary, initSentry } from "@nens/sentry";

import { AttemptLogin } from "./components/AttemptLogin";
import DownloadScreen from "./DownloadScreen/DownloadScreen";
import MainScreen from "./MainScreen/MainScreen";
import WebsocketMiddleware from "./reduxMiddleware/WebsocketMiddleware";
import {
  PossibleStartWithModelAndOrganisation,
  QuickstartWithSimulation,
  QuickstartWithSimulationTemplateAndOrganisation,
} from "./StartScreen/Quickstart";
import StartScreen from "./StartScreen/StartScreen";
import StartupScreen from "./StartScreen/StartupScreen";
import * as reducers from "./state/";
import { getHighLevelState } from "./state/appState";
import { areOrgansationsFetched, getUser, isAuthenticated } from "./state/session";
import { Websocket } from "./state/websockets";
import { getLocaleStringFromBrowserSetting } from "./utils/detectLanguage";

export const APP_BASE_HREF = "/live";

initSentry();

// Create the Redux store
const rootReducer = combineReducers(reducers);

// There are several Websockets, define them here:

const activeSimulationsMiddleware = reduxWebsocket({
  prefix: Websocket.ActiveSimulations,
});
const mainWebsocketMiddleware = reduxWebsocket({
  prefix: Websocket.Main,
});
const waterdepthsWebsocketMiddleware = reduxWebsocket({
  prefix: Websocket.Waterdepths,
});
const waterlevelChartMiddleware = reduxWebsocket({
  prefix: Websocket.WaterlevelChart,
});
const waterFlowChartChartMiddleware = reduxWebsocket({
  prefix: Websocket.WaterFlowChart,
});
const waterLevelChartPointMiddleware = reduxWebsocket({
  prefix: Websocket.WaterlevelChartPoint,
});
const groundWaterChartPointMiddleware = reduxWebsocket({
  prefix: Websocket.GroundWaterChartPoint,
});
const waterlevelChartProfileMiddleware = reduxWebsocket({
  prefix: Websocket.WaterlevelProfileChart,
});
const pumpDischargeChartMiddleware = reduxWebsocket({
  prefix: Websocket.PumpDischargeChart,
});
const breachChartMiddleware = reduxWebsocket({
  prefix: Websocket.BreachChart,
});
const rainGraphOnPointChartMiddleware = reduxWebsocket({
  prefix: Websocket.RainGraphOnPointChart,
});

declare global {
  // Add the potentially present Redux devtools extension to the type of the 'window' object
  // so that it can be checked below without complaints from Typescript.
  interface Window {
    __REDUX_DEVTOOLS_EXTENSION__?: typeof compose;
  }
}

const store = createStore(
  rootReducer,
  compose(
    applyMiddleware(thunk),
    applyMiddleware(activeSimulationsMiddleware),
    applyMiddleware(mainWebsocketMiddleware),
    applyMiddleware(waterdepthsWebsocketMiddleware),
    applyMiddleware(waterlevelChartMiddleware),
    applyMiddleware(waterLevelChartPointMiddleware),
    applyMiddleware(groundWaterChartPointMiddleware),
    applyMiddleware(waterFlowChartChartMiddleware),
    applyMiddleware(waterlevelChartProfileMiddleware),
    applyMiddleware(pumpDischargeChartMiddleware),
    applyMiddleware(breachChartMiddleware),
    applyMiddleware(rainGraphOnPointChartMiddleware),
    applyMiddleware(WebsocketMiddleware),
    ((window as any).__REDUX_DEVTOOLS_EXTENSION__ &&
      (window as any).__REDUX_DEVTOOLS_EXTENSION__()) ||
      // Add compose here so it doesnot crash when redux devtools not present
      // https://github.com/zalmoxisus/redux-devtools-extension/issues/320
      compose
  )
);

export type AppState = ReturnType<typeof store.getState>;
export type AppGetState = typeof store.getState;
export type AppDispatch = ThunkDispatch<AppState, undefined, AnyAction>;
export type Thunk = ThunkAction<void, AppState, undefined, AnyAction>;
export const appDispatch = store.dispatch as AppDispatch;

// Create a wrapper with a Provider around the main App component

const App: React.FC = () => {
  const queryClient = new QueryClient();
  return (
    <SentryErrorBoundary appName="hcc-live-site" FallbackComponent={SystemStatusPage}>
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <ChakraProvider theme={getRanaTheme("light")}>
            <Fonts fontsPath={`${APP_BASE_HREF}/assets/fonts`} />
            <Routes>
              <Route
                path={`${APP_BASE_HREF}/auth/callback/cognito`}
                element={<CognitoCallbackPage oauthRedirectUrlPrefix={APP_BASE_HREF} />}
              />

              {/* Note there is no logout callback in the live site. There is no logout button, only a link back to Rana proper. */}
              {/* Define the main application routes here */}
              <Route path="*" element={<AppWithAuthentication />} />
            </Routes>
          </ChakraProvider>
        </Provider>
      </QueryClientProvider>
    </SentryErrorBoundary>
  );
};

// Wrapper that ensures we are correctly logged in

const AppWithAuthentication: React.FC = () => {
  // Choose which main component to render based on the app flow
  const authenticated = useSelector(isAuthenticated);
  const organisationsFetched = useSelector(areOrgansationsFetched);
  const user = useSelector(getUser);
  const localeProp = getLocaleStringFromBrowserSetting();

  // In order to provide the same login flow as Rana itself for users
  // coming directly to the HCC live site, we login using both Rana's
  // "TenantProvider" and the live site's "AttemptLogin" (which stores
  // auth information with Redux).

  // To login with TenantProvider, we need to know a tenant to login to.
  // In Rana proper that is determined by the URL, but for the live site we get it
  // from localstorage if it is set there (by the user logging in to Rana previously).
  // If no tenant is found, we show the login screen where the user is aked to
  // enter a tenant code.

  const [tenant, setTenant] = React.useState<string | null>(
    retrieveTenantAndOrganisation().tenant ?? null
  );

  if (!tenant) {
    return (
      <SystemStatusPage
        variant="welcome"
        chooseTenantCode={(tenantCode: string) => {
          storeTenantAndOrganisation(tenantCode, []);
          setTenant(tenantCode);
        }}
      />
    );
  } else {
    return (
      <TenantProvider
        tenant={tenant}
        notLoggedInRoutes={(setIdentityProvider) => (
          <IdentityProviderChooser tenant={tenant} setIdentityProvider={setIdentityProvider} />
        )}
        oauthRedirectUrlPrefix="/live"
      >
        <div
          // better support for Vietnamese characters
          style={{ fontFamily: localeProp === "vi" ? "Times New Roman" : undefined }}
        >
          {authenticated && user && organisationsFetched ? <Application /> : <AttemptLogin />}
        </div>
      </TenantProvider>
    );
  }
};

// The actual application

const Application: React.FC = () => {
  const appState = useSelector(getHighLevelState);
  if (appState === "MAIN" || appState === "STARTING UP")
    return (
      <>
        {appState === "STARTING UP" && <StartupScreen hideBackground />}
        <MainScreen />
      </>
    );
  if (appState === "DOWNLOAD") return <DownloadScreen />;
  // this is always case "START", but default is more defensive
  // Other situations should probably also be done using react-router in the future
  return (
    <Routes>
      <Route path={`${APP_BASE_HREF}/`} element={<StartScreen />} />
      <Route
        path={`${APP_BASE_HREF}/quickstart/model-and-organisation/:modelId/:organisationId/`}
        element={<PossibleStartWithModelAndOrganisation />}
      />
      <Route
        path={`${APP_BASE_HREF}/quickstart/simulation/:simulationId/`}
        element={
          <>
            <StartupScreen />
            <QuickstartWithSimulation />
          </>
        }
      />
      <Route
        path={`${APP_BASE_HREF}/quickstart/simulation-template/:modelId/:simulationTemplateId/:organisationId/`}
        element={
          <>
            <StartupScreen />
            <QuickstartWithSimulationTemplateAndOrganisation />
          </>
        }
      />
    </Routes>
  );
};

export default App;
